import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { bitcommLogo } from "../../DataGrid/PdfImages";

/**
 * Export Unified Dashboard data to Excel (CSV format)
 * CSV can be opened in Excel with proper formatting
 */
export const exportToExcel = (data, filename = "unified_dashboard_export") => {
  const { stats, divisions, stations, systems } = data;

  // Create workbook content as CSV
  let csvContent = "";

  // Add BOM for Excel UTF-8 compatibility
  csvContent += "\uFEFF";

  // Title and timestamp
  const timestamp = new Date().toLocaleString();
  csvContent += `Unified Alarm Dashboard Report\n`;
  csvContent += `Generated: ${timestamp}\n\n`;

  // Summary Statistics Section
  csvContent += "=== SUMMARY STATISTICS ===\n";
  csvContent += "Metric,Value\n";
  csvContent += `Total Alarms,${stats.totalAlarms}\n`;
  csvContent += `Predictive Failure (PF),${stats.predictiveFailure}\n`;
  csvContent += `Failure (FL),${stats.failure}\n`;
  csvContent += `Maintenance Overdue (MO),${stats.maintenanceOverdue}\n`;
  csvContent += `Maintenance Deficiency (MF),${stats.maintenanceDeficiency}\n`;
  csvContent += `Total Stations,${stats.totalStations}\n`;
  csvContent += `Affected Stations,${stats.affectedStations}\n`;
  csvContent += `Healthy Stations,${stats.healthyStations}\n\n`;

  // Division-wise Alarms Section
  csvContent += "=== DIVISION-WISE ALARMS ===\n";
  csvContent += "Division,Predictive Failure,Failure,Maintenance Overdue,Maintenance Deficiency,Total Assets,Total Alarms\n";

  Object.entries(divisions || {}).forEach(([divCode, divData]) => {
    const total = (divData.pf_cnt || 0) + (divData.fl_cnt || 0) + (divData.mo_cnt || 0) + (divData.mf_cnt || 0);
    csvContent += `${divCode},${divData.pf_cnt || 0},${divData.fl_cnt || 0},${divData.mo_cnt || 0},${divData.mf_cnt || 0},${divData.total_asset_cnt || 0},${total}\n`;
  });
  csvContent += "\n";

  // Station-wise Alarms Section
  csvContent += "=== STATION-WISE ALARMS ===\n";
  csvContent += "Station,Predictive Failure,Failure,Maintenance Overdue,Maintenance Deficiency,Total Assets,Total Alarms\n";

  Object.entries(stations || {}).forEach(([stnCode, stnData]) => {
    const total = (stnData.pf_cnt || 0) + (stnData.fl_cnt || 0) + (stnData.mo_cnt || 0) + (stnData.mf_cnt || 0);
    csvContent += `${stnCode},${stnData.pf_cnt || 0},${stnData.fl_cnt || 0},${stnData.mo_cnt || 0},${stnData.mf_cnt || 0},${stnData.total_asset_cnt || 0},${total}\n`;
  });
  csvContent += "\n";

  // System-wise Alarms Section
  csvContent += "=== SYSTEM-WISE ALARMS ===\n";
  csvContent += "System Code,System Name,Total Alarms\n";

  Object.entries(systems || {}).forEach(([sysCode, sysData]) => {
    const totalAlarms = Object.values(sysData.counts || {}).reduce((sum, cnt) => sum + cnt, 0);
    csvContent += `${sysCode},${sysData.software_alias || sysCode},${totalAlarms}\n`;
  });

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}_${formatDateForFilename()}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export Unified Dashboard data to PDF
 */
export const exportToPDF = (data, filename = "unified_dashboard_export") => {
  const { stats, divisions, stations, systems } = data;

  // Create PDF document (landscape for more columns)
  const doc = new jsPDF("l", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const timestamp = new Date().toLocaleString();

  // Add logo and header
  try {
    doc.addImage(bitcommLogo, "JPEG", 30, 15, 80, 30);
  } catch (e) {
    console.warn("Logo not loaded:", e);
  }

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Unified Alarm Dashboard Report", pageWidth / 2, 35, { align: "center" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${timestamp}`, pageWidth - 30, 25, { align: "right" });

  let startY = 60;

  // Summary Statistics Table
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Summary Statistics", 30, startY);
  startY += 10;

  const summaryData = [
    ["Total Alarms", stats.totalAlarms.toString()],
    ["Predictive Failure (PF)", stats.predictiveFailure.toString()],
    ["Failure (FL)", stats.failure.toString()],
    ["Maintenance Overdue (MO)", stats.maintenanceOverdue.toString()],
    ["Maintenance Deficiency (MF)", stats.maintenanceDeficiency.toString()],
    ["Total Stations", stats.totalStations.toString()],
    ["Affected Stations", stats.affectedStations.toString()],
    ["Healthy Stations", stats.healthyStations.toString()],
  ];

  autoTable(doc, {
    startY: startY,
    head: [["Metric", "Value"]],
    body: summaryData,
    theme: "grid",
    headStyles: {
      fillColor: [52, 152, 219],
      textColor: 255,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 200, fontStyle: "bold" },
      1: { cellWidth: 100, halign: "center" },
    },
    styles: {
      fontSize: 9,
      cellPadding: 5,
    },
    margin: { left: 30 },
  });

  startY = doc.lastAutoTable.finalY + 20;

  // Division-wise Alarms Table
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Division-wise Alarms", 30, startY);
  startY += 10;

  const divisionData = Object.entries(divisions || {}).map(([divCode, divData]) => {
    const total = (divData.pf_cnt || 0) + (divData.fl_cnt || 0) + (divData.mo_cnt || 0) + (divData.mf_cnt || 0);
    return [
      divCode,
      (divData.pf_cnt || 0).toString(),
      (divData.fl_cnt || 0).toString(),
      (divData.mo_cnt || 0).toString(),
      (divData.mf_cnt || 0).toString(),
      (divData.total_asset_cnt || 0).toString(),
      total.toString(),
    ];
  });

  if (divisionData.length > 0) {
    autoTable(doc, {
      startY: startY,
      head: [["Division", "PF", "FL", "MO", "MF", "Total Assets", "Total Alarms"]],
      body: divisionData,
      theme: "grid",
      headStyles: {
        fillColor: [46, 204, 113],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center", fontStyle: "bold" },
      },
      styles: {
        fontSize: 8,
        cellPadding: 4,
      },
      margin: { left: 30, right: 30 },
    });
    startY = doc.lastAutoTable.finalY + 20;
  }

  // Check if we need a new page
  if (startY > doc.internal.pageSize.getHeight() - 100) {
    doc.addPage();
    startY = 40;
  }

  // Station-wise Alarms Table
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Station-wise Alarms", 30, startY);
  startY += 10;

  const stationData = Object.entries(stations || {}).map(([stnCode, stnData]) => {
    const total = (stnData.pf_cnt || 0) + (stnData.fl_cnt || 0) + (stnData.mo_cnt || 0) + (stnData.mf_cnt || 0);
    return [
      stnCode,
      (stnData.pf_cnt || 0).toString(),
      (stnData.fl_cnt || 0).toString(),
      (stnData.mo_cnt || 0).toString(),
      (stnData.mf_cnt || 0).toString(),
      (stnData.total_asset_cnt || 0).toString(),
      total.toString(),
    ];
  });

  if (stationData.length > 0) {
    autoTable(doc, {
      startY: startY,
      head: [["Station", "PF", "FL", "MO", "MF", "Total Assets", "Total Alarms"]],
      body: stationData,
      theme: "grid",
      headStyles: {
        fillColor: [155, 89, 182],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { fontStyle: "bold" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center", fontStyle: "bold" },
      },
      styles: {
        fontSize: 8,
        cellPadding: 4,
      },
      margin: { left: 30, right: 30 },
      showHead: "everyPage",
    });
    startY = doc.lastAutoTable.finalY + 20;
  }

  // Check if we need a new page for systems
  if (startY > doc.internal.pageSize.getHeight() - 100) {
    doc.addPage();
    startY = 40;
  }

  // System-wise Alarms Table
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("System-wise Alarms", 30, startY);
  startY += 10;

  const systemData = Object.entries(systems || {}).map(([sysCode, sysData]) => {
    const totalAlarms = Object.values(sysData.counts || {}).reduce((sum, cnt) => sum + cnt, 0);
    return [
      sysCode,
      sysData.software_alias || sysCode,
      totalAlarms.toString(),
    ];
  });

  if (systemData.length > 0) {
    autoTable(doc, {
      startY: startY,
      head: [["System Code", "System Name", "Total Alarms"]],
      body: systemData,
      theme: "grid",
      headStyles: {
        fillColor: [230, 126, 34],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { fontStyle: "bold", halign: "center" },
        1: { halign: "left" },
        2: { halign: "center", fontStyle: "bold" },
      },
      styles: {
        fontSize: 9,
        cellPadding: 5,
      },
      margin: { left: 30, right: 30 },
    });
  }

  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 15,
      { align: "center" }
    );
  }

  // Save the PDF
  doc.save(`${filename}_${formatDateForFilename()}.pdf`);
};

/**
 * Format date for filename (YYYYMMDD_HHmmss)
 */
const formatDateForFilename = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};
